import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Alert,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Edit as EditIcon, AttachFile as AttachFileIcon, CheckBox as CheckBoxIcon, Tune as FieldsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AttachmentList from './AttachmentList';
import SubtaskList from './SubtaskList';
import CustomFieldInput from './CustomFieldInput';
import Divider from '@mui/material/Divider';

function TaskForm({ open, onClose, onSubmit, task, isEdit, projectMembers, projectPhases, onSubtaskChange, projectCustomFields }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const PRIORITY_OPTIONS = [
    { value: 'low', label: t('tasks.priorityLow') },
    { value: 'medium', label: t('tasks.priorityMedium') },
    { value: 'high', label: t('tasks.priorityHigh') },
    { value: 'urgent', label: t('tasks.priorityUrgent') }
  ];

  const STATUS_OPTIONS = [
    { value: 'not_started', label: t('tasks.statusNotStarted') },
    { value: 'in_progress', label: t('tasks.statusInProgress') },
    { value: 'completed', label: t('tasks.statusCompleted') },
    { value: 'blocked', label: t('tasks.statusBlocked') }
  ];

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee_id: null,
    assignee_ids: [],
    phase: '',
    start_date: '',
    end_date: '',
    status: 'not_started',
    priority: 'medium',
    progress: 0,
    estimated_hours: '',
    custom_fields: {}
  });
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  const buildCustomFieldsMap = (cfValues) => {
    if (!cfValues || !Array.isArray(cfValues)) return {};
    return cfValues.reduce((acc, v) => { acc[v.field_definition_id] = v.value; return acc; }, {});
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [fieldId]: value } }));
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('era_user'));
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    if (task && isEdit) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        assignee_id: task.assignee_id || null,
        assignee_ids: task.assignees ? task.assignees.map(a => a.id) : (task.assignee_id ? [task.assignee_id] : []),
        phase: task.phase || '',
        start_date: task.start_date ? task.start_date.split('T')[0] : '',
        end_date: task.end_date ? task.end_date.split('T')[0] : '',
        status: task.status || 'not_started',
        priority: task.priority || 'medium',
        progress: task.progress || 0,
        estimated_hours: task.estimated_hours || '',
        custom_fields: buildCustomFieldsMap(task.custom_field_values)
      });
    } else {
      setFormData({
        title: '',
        description: '',
        assignee_id: null,
        assignee_ids: [],
        phase: '',
        start_date: '',
        end_date: '',
        status: 'not_started',
        priority: 'medium',
        progress: 0,
        estimated_hours: '',
        custom_fields: {}
      });
      setTabValue(0);
    }
    setError('');
  }, [task, isEdit, open]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError(t('tasks.titleRequired'));
      return false;
    }

    if (formData.title.trim().length < 3) {
      setError(t('tasks.titleMinLength'));
      return false;
    }

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        setError(t('tasks.endDateAfterStart'));
        return false;
      }
    }

    if (formData.progress < 0 || formData.progress > 100) {
      setError(t('tasks.progressRange'));
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      assignee_id: formData.assignee_id || null,
      assignee_ids: formData.assignee_ids || [],
      phase: formData.phase || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null
    };

    onSubmit(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box>
               {isEdit ? t('tasks.editTask') : t('tasks.createTask')}
            </Box>
             {isEdit && (
               <Tabs
                 value={tabValue}
                 onChange={(e, v) => setTabValue(v)}
                 aria-label="task tabs"
                 sx={{ minHeight: 0 }}
               >
                  <Tab icon={<EditIcon fontSize="small" />} iconPosition="start" label={t('tasks.detailsTab')} sx={{ minHeight: 0, py: 1 }} />
                  <Tab icon={<CheckBoxIcon fontSize="small" />} iconPosition="start" label={`${t('tasks.subtasksTab')}${task && task.subtasks_count > 0 ? ` (${task.subtasks_count})` : ''}`} sx={{ minHeight: 0, py: 1 }} />
                  <Tab icon={<AttachFileIcon fontSize="small" />} iconPosition="start" label={t('tasks.filesTab')} sx={{ minHeight: 0, py: 1 }} />
               </Tabs>
             )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, height: tabValue >= 1 ? '500px' : 'auto' }}>
          {error && tabValue === 0 && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {tabValue === 0 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('tasks.titleLabel')}
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    autoFocus
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('common.description')}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    options={projectMembers || []}
                    getOptionLabel={(option) => option.name || option.full_name || ''}
                    value={(projectMembers || []).filter(m => formData.assignee_ids.includes(m.id))}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        assignee_ids: newValue.map(v => v.id),
                        assignee_id: newValue.length > 0 ? newValue[0].id : null
                      });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('tasks.assignedTo')} placeholder={t('tasks.addMembers')} />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.name || option.full_name}
                          size="small"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={projectPhases || []}
                    value={formData.phase}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, phase: newValue });
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFormData({ ...formData, phase: newInputValue });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t('tasks.phase')} fullWidth />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('tasks.startDate')}
                    name="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('tasks.endDate')}
                    name="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tasks.priority')}</InputLabel>
                    <Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      label={t('tasks.priority')}
                    >
                      {PRIORITY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('tasks.status')}</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      label={t('tasks.status')}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('tasks.progress')}
                    name="progress"
                    type="number"
                    value={formData.progress}
                    onChange={handleChange}
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('tasks.estimatedHours')}
                    name="estimated_hours"
                    type="number"
                    value={formData.estimated_hours}
                    onChange={handleChange}
                  />
                </Grid>

                {/* ── Champs personnalisés ── */}
                {projectCustomFields && projectCustomFields.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FieldsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <span style={{ fontSize: '0.75rem', color: '#757575' }}>{t('tasks.customFields')}</span>
                        </Box>
                      </Divider>
                    </Grid>
                    {projectCustomFields.map(field => (
                      <Grid item xs={12} sm={field.field_type === 'checkbox' ? 12 : 6} key={field.id}>
                        <CustomFieldInput
                          field={field}
                          value={formData.custom_fields[field.id] ?? ''}
                          onChange={handleCustomFieldChange}
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            </Box>
          )}

          {tabValue === 1 && isEdit && task && (
            <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
              <SubtaskList task={task} projectMembers={projectMembers} onSubtaskChange={onSubtaskChange} />
            </Box>
          )}

          {tabValue === 2 && isEdit && (
            <Box sx={{ p: 3 }}>
              <AttachmentList relatedType="task" relatedId={task.id} />
            </Box>
          )}
        </DialogContent>

        {tabValue === 0 && (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={onClose} color="inherit">
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {isEdit ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        )}

        {(tabValue === 1 || tabValue === 2) && (
           <DialogActions sx={{ px: 3, pb: 3 }}>
             <Button onClick={onClose} color="inherit">
               {t('common.close')}
             </Button>
           </DialogActions>
        )}
      </Box>
    </Dialog>
  );
}

export default TaskForm;
